import { GenericBadgeV2, Icon, InputState } from "@studio3t/generic-components";

import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { schemasApi } from "@/api/client";
import { ConfirmDeleteModal } from "@/components/ConfirmDeleteModal";
import { JumpingDots } from "@/components/JumpingDots";
import {
  useSaveSchema,
  useSchema,
  useSchemasByService,
  useServices,
} from "@/hooks/useApi";
import { schemaCapableServices } from "@/utils/schemaServices";
import { jsonErrorRows, schemaTextProblem } from "./editorErrors";
import { useInlineRename } from "./inlineRename";
import { NameModal } from "./NameModal";
import {
  ExplorerDropZone,
  LeadingIcon,
  RemoveRowButton,
  RowActionButton,
  RowActionsGroup,
  toggleActivationProps,
} from "./PipelineNavBar";
import {
  isDefaultUntitledName,
  parseSchemaRowId,
  type StagedResource,
  type StagedSchema,
  schemaInstanceId,
  schemaRowId,
  usePipelineStaging,
} from "./pipelineStaging";
import { workspaceNameTaken as nameTaken } from "./saveWorkspace";
import { detectSchemaEncoding, schemaContentEqual } from "./schemaEncoding";
import { schemaForkService, schemaHomeService } from "./schemaHome";
import { slotSeeding } from "./schemaSeeding";
import { IndentArrowIcon } from "./sectionIcons";
import {
  AccentBar,
  ItemRow,
  PseudoText,
  SchemaBadgeSlot,
  SchemaIndent,
  SchemaItemBody,
  StageAlert,
  SubSection,
} from "./stageRowChrome";

// The SCHEMA sub-section of a stage block (Figma 15692-316963): the
// staged schema rows with inline rename and save/save-as flows, and the
// placeholder/drop slot while the block's schema slot is empty. Every row
// opens with the ↳ glyph and a "schema" badge (14327-153124 "Schema"
// variant) — there is no separate SCHEMA header. Extracted from
// StageNavPanel.

// The ↳ + "schema" badge prefix shared by every schema row. The glyph
// darkens only on the selected placeholder row (15683-307340).
function SchemaRowPrefix({ emphasis = false }: { emphasis?: boolean }) {
  return (
    <>
      <SchemaIndent $emphasis={emphasis}>
        <IndentArrowIcon />
      </SchemaIndent>
      <SchemaBadgeSlot>
        <GenericBadgeV2 content="schema" color="secondary" emphasis="subtle" />
      </SchemaBadgeSlot>
    </>
  );
}

// attachments plus a bottom-block waiting slot. Saving edits under the
// same id changes all of them — above one reference the row Save warns
// first (Continue = save everywhere, Save as = fork a new schema for
// this block only).
// Lightweight domain aliases for the primitive arguments below (the
// pipelineLint pattern) — they name what a string means at a call site
// without runtime cost.
type SchemaName = string;
type SchemaBody = string;
type RowId = string;
type TestId = string;

const referenceCount = (schema: StagedSchema) =>
  schema.attachments.length + (schema.pending?.length ?? 0);

// Save refuses what Deploy would refuse (TTTL-216): a schema text that is
// empty or has a JSON error never reaches the store, where it would
// deploy as stored. The rows disable their Save with the reason; the
// save paths keep the guard for ⌘S and the modal.
const SCHEMA_SAVE_REASONS = {
  empty: "Add schema content before saving",
  invalid: "Fix the schema's JSON error before saving",
} as const;

/** The reason Save is refused for this text, or null when it may save. */
function schemaSaveReason(text: SchemaBody): string | null {
  const problem = schemaTextProblem(text);
  return problem === null ? null : SCHEMA_SAVE_REASONS[problem];
}

function rejectInvalidSchema(text: SchemaBody): void {
  const reason = schemaSaveReason(text);
  if (reason !== null) throw new Error(reason);
}

// Nested SCHEMA sub-section (rows + placeholder). The placeholder
// row swaps to a schema drop zone while a schema drag runs; otherwise it's
// the selectable "use the editor" pseudo-row (Figma 14398-200858) that
// opens the schema editor in the editor panel.
// Everything schema saving needs: the schema-capable services and their
// stored names for the duplicate checks, and the two save flows — the
// direct Save / the Save-as modal persist a new schema and stage it into
// the sub-section (attached to its stage when the sub-section has one);
// the row Save persists edits of an already-staged schema under its
// existing id. Schemas live per service (TTTL-319): a NEW schema goes to
// the block's home service (schemaHome.ts); a row's "Save as" fork, its
// edits, rename and update go to the service its id names.
function useSchemaSaving(
  editorRowId: RowId,
  selectedId: string | null,
  draftText: SchemaBody,
  onStageSaved: (resource: StagedResource) => void,
) {
  const { markSchemaSaved, clearSchemaDraft, renameSchema } =
    usePipelineStaging();
  const { data: services } = useServices();
  const homeService = schemaHomeService(editorRowId, services);
  const capableServices = useMemo(
    () => schemaCapableServices(services).map((s) => s.name),
    [services],
  );
  // Existing schema names per service for the duplicate checks — the
  // backend refuses a name taken within one service's store, and the
  // modal / row input is the guard that explains it.
  const { byService } = useSchemasByService(capableServices);
  const storedIn = (serviceName: string | null) =>
    serviceName === null ? [] : (byService[serviceName]?.data ?? []);
  const { mutateAsync: saveSchema } = useSaveSchema();

  // Persists a brand-new schema entry on a service and returns it as a
  // stageable resource — shared by the direct save and the "Save as" fork.
  const persistNew = async (
    serviceName: string | null,
    name: SchemaName,
    definition: SchemaBody,
  ): Promise<StagedResource | null> => {
    if (!serviceName) {
      throw new Error("No schema-capable service available");
    }
    rejectInvalidSchema(definition);
    const result = await saveSchema({
      serviceName,
      entry: {
        name,
        encoding: detectSchemaEncoding(definition) ?? "json",
        definition,
      },
    });
    if (!result) return null;
    return { id: schemaRowId(serviceName, result.id), name, icon: Icon.Schema };
  };

  const saveNew = async (name: SchemaName) => {
    const resource = await persistNew(homeService, name, draftText);
    if (resource) {
      // Staging selects the new row and re-loads the saved definition into
      // the editor, so the created schema stays on screen (14442-316243).
      onStageSaved(resource);
    }
  };

  // The row's "Save as" fork: the edited content (the row's instance
  // draft) becomes a NEW schema in the folder the source came from — its
  // own service, under the new name — so the original (used elsewhere in
  // the pipeline) stays untouched.
  const saveAs = async (
    source: StagedSchema,
    name: SchemaName,
    definition: SchemaBody,
  ) => {
    return persistNew(schemaForkService(source.id), name, definition);
  };

  // Persists a row's edits from its instance draft — the draft mirrors the
  // editor while the row is selected and remembers it afterwards, so Save
  // works on unselected dirty rows too (the button is disabled without a
  // draft).
  const saveRow = async (
    schema: StagedSchema,
    instanceId: RowId,
    definition: SchemaBody,
  ) => {
    const owner = parseSchemaRowId(schema.id);
    if (!owner || schemaSaveReason(definition) !== null) return;
    await saveSchema({
      serviceName: owner.serviceName,
      entry: {
        id: owner.entryId,
        name: schema.name,
        encoding: detectSchemaEncoding(definition) ?? "json",
        definition,
      },
    });
    // Only the selected row's content is on screen — its saved text is the
    // new dirty baseline; other rows just lose their obsolete draft.
    if (selectedId === instanceId) markSchemaSaved();
    clearSchemaDraft(instanceId);
  };

  // A stored schema already carrying the name on the home service — the
  // placeholder row's direct Save then UPDATES it under its existing id
  // (editing an existing schema) instead of demanding another name.
  const storedIdFor = (name: SchemaName): string | null =>
    storedIn(homeService).find((entry) => entry.name === name)?.id ?? null;

  const saveOver = async (name: SchemaName) => {
    const id = storedIdFor(name);
    if (!id || !homeService) return;
    rejectInvalidSchema(draftText);
    await saveSchema({
      serviceName: homeService,
      entry: {
        id,
        name,
        encoding: detectSchemaEncoding(draftText) ?? "json",
        definition: draftText,
      },
    });
    onStageSaved({ id: schemaRowId(homeService, id), name, icon: Icon.Schema });
  };

  // Whether a service's store already holds the name — the home service,
  // or the Save-as source's own service.
  const validateName = (trimmed: SchemaName, serviceName = homeService) =>
    nameTaken(
      trimmed,
      storedIn(serviceName).flatMap((s) => (s.name ? [{ name: s.name }] : [])),
    )
      ? {
          state: InputState.Warning,
          messages: ["A schema with this name already exists"],
        }
      : null;

  // Renames a staged schema in place: same entry id, stored definition and
  // encoding untouched — only the name changes, in the backend store and in
  // every block that renders the schema. Content drafts key on the id, so
  // dirty rows keep their edits through a rename. The staged rows rename
  // right after the backend confirms (the row's input stays up — disabled —
  // for the request's duration); a failure reports false so the row can
  // offer a retry.
  const renameRow = async (
    schema: StagedSchema,
    name: SchemaName,
  ): Promise<boolean> => {
    const owner = parseSchemaRowId(schema.id);
    if (!owner) return false;
    try {
      const entry = await schemasApi.get(owner.serviceName, owner.entryId);
      if (typeof entry?.definition !== "string")
        throw new Error("Schema entry not found");
      await saveSchema({
        serviceName: owner.serviceName,
        entry: {
          id: owner.entryId,
          name,
          encoding:
            entry.encoding ?? detectSchemaEncoding(entry.definition) ?? "json",
          definition: entry.definition,
        },
      });
      renameSchema(schema.id, name);
      return true;
    } catch {
      return false;
    }
  };

  return {
    homeService,
    saveNew,
    saveAs,
    saveRow,
    renameRow,
    validateName,
    storedIdFor,
    saveOver,
  };
}

// Staged schema row in a SCHEMA sub-section (Figma 15692-316966
// "usersAtlasDedicatedSource"): ↳ + "schema" badge, then the 40px
// List/item body — Schema icon, textbody name (dirty asterisk while the
// editor content differs from the stored definition), trailing Save +
// remove.
// The active treatment (tint + 2px accent + green icon) only shows while
// the row is deliberately selected — selecting it loads the schema's
// definition into the editor.
function StagedSchemaRow({
  schema,
  selected,
  dirty,
  saveBlocked,
  errored,
  warning,
  onToggleSelected,
  onSave,
  onUnstage,
  onRename,
  validateRename,
}: {
  schema: StagedSchema;
  selected: boolean;
  dirty: boolean;
  /** Why the row's draft can't be saved (empty, JSON error), or null:
   *  Save disables, naming the reason. */
  saveBlocked: string | null;
  /** The row's editor draft holds a JSON error — the leading icon goes
   *  red (TTTL-215). */
  errored: boolean;
  /** Run would ignore this schema (unreferenced, see the panel alert) —
   *  the leading icon goes to the yellow warning glyph (Figma
   *  15695-132722). An error outranks it. */
  warning: boolean;
  /** Toggles this row's per-block instance selection. */
  onToggleSelected: () => void;
  onSave: () => void;
  onUnstage: (id: RowId) => void;
  /** Persists a new name for the schema (backend + staged rows); resolves
   *  false when the save failed. */
  onRename: (name: SchemaName) => Promise<boolean>;
  /** Duplicate check — an error message, or null when the name is free. */
  validateRename: (name: string) => string | null;
}) {
  const rename = useInlineRename({
    name: schema.name,
    dirty,
    testId: "schema-rename-input",
    onRename,
    validateRename,
    // Switching rows closes an open name input (re-open via
    // double-click).
    selected,
    ensureSelected: () => {
      if (!selected) onToggleSelected();
    },
  });
  return (
    <ItemRow
      data-testid="stage-tab-schema-item"
      data-resource-id={schema.id}
      $active={selected}
      $schema
      $interactive
      {...toggleActivationProps(onToggleSelected)}
    >
      <SchemaRowPrefix />
      <SchemaItemBody>
        <LeadingIcon
          icon={Icon.Schema}
          active={selected}
          errored={errored}
          warning={warning}
        />
        {rename.nameNode}
      </SchemaItemBody>
      <RowActionsGroup>
        {/* Save is live while the row is dirty, and during an inline
            rename: pressing it blurs the input, which commits the typed
            name (the click itself then finds nothing to save). */}
        <RowActionButton
          label={saveBlocked ?? `Save ${schema.name}`}
          icon={Icon.Save}
          onClick={onSave}
          disabled={rename.saveDisabled || saveBlocked !== null}
        />
        <RemoveRowButton
          name={schema.name}
          onRemove={() => onUnstage(schema.id)}
        />
      </RowActionsGroup>
      {selected && <AccentBar />}
    </ItemRow>
  );
}

// The untitled row's Save button: what it says and whether it presses —
// the refusal reason wins, then the already-saved state.
function untitledSaveButton(state: {
  name: string;
  blocked: string | null;
  alreadySaved: boolean;
  renameSaveDisabled: boolean;
  saving: boolean;
}): { label: string; disabled: boolean } {
  const { name, blocked, alreadySaved } = state;
  const label =
    blocked ?? (alreadySaved ? `${name} is already saved` : `Save ${name}`);
  const disabled =
    state.renameSaveDisabled ||
    state.saving ||
    alreadySaved ||
    blocked !== null;
  return { label, disabled };
}

// The unsaved "Untitled schema*" row (Figma 14502-129720) — typing into the
// schema editor turns the selected pseudo-row into it until the content is
// saved or discarded. Same naming flow as the untitled script/test rows:
// the name input opens hands-off on the mint (focus stays in the schema
// editor), the double-click renames the DRAFT locally, and the Save modal
// prefills a custom name.
function UntitledSchemaRow({
  testId,
  homeService,
  name,
  draft,
  selected,
  startNaming,
  onStartNamingConsumed,
  onToggle,
  onRename,
  validateRename,
  onSave,
  onDiscard,
  warning = false,
}: {
  testId: TestId;
  /** The service the draft saves into (schemaHome.ts) — where the
   *  already-saved check looks. */
  homeService: string | null;
  name: string;
  /** The slot draft's text — the already-saved check compares it against
   *  the name-matched stored schema. */
  draft: SchemaBody;
  selected: boolean;
  /** Mounts with the name input open, hands-off (a just-minted draft). */
  startNaming: boolean;
  onStartNamingConsumed: () => void;
  onToggle: () => void;
  /** Renames the draft locally (client-side only). */
  onRename: (name: string) => Promise<boolean>;
  validateRename: (name: string) => string | null;
  /** Saves the draft under this name; resolves an error message to show
   *  on the name input, or null on success. */
  onSave: (name: string) => Promise<string | null>;
  onDiscard: () => void;
  /** Run would ignore the draft (an unreferenced bottom block) — warning
   *  glyph unless the draft's JSON error outranks it. */
  warning?: boolean;
}) {
  const [saving, setSaving] = useState(false);
  // Parsed per draft change, not per render (TTTL-215's red icon).
  const errored = useMemo(() => jsonErrorRows(draft).length > 0, [draft]);
  // Any unsaved draft carries the star — custom-named ones (a rename, the
  // dropped source's seeded schema) included — EXCEPT one whose exact
  // shape is already stored under the row's name: that row IS the saved
  // schema, so it reads clean (and its Save disables below).
  const alreadySavedAsNamed = useDraftAlreadySaved(homeService, name, draft);
  const unnamed = isDefaultUntitledName(name, "Untitled schema");
  const rename = useInlineRename({
    name,
    dirty: true,
    star: !alreadySavedAsNamed,
    testId: `${testId}-untitled-rename-input`,
    onRename,
    validateRename,
    // Switching rows closes the naming input; while the row is still
    // un-named, coming back re-opens it (the standing affordance).
    selected,
    reopenOnSelect: unnamed,
    ensureSelected: () => {
      if (!selected) onToggle();
    },
    startEditing: startNaming,
  });
  // Consume the fresh-mint flag so a later remount (tab switch) shows
  // the plain label again.
  useEffect(() => {
    if (startNaming) onStartNamingConsumed();
  }, [startNaming, onStartNamingConsumed]);
  const alreadySaved = useDraftAlreadySaved(
    homeService,
    rename.pendingName,
    draft,
  );
  const saveButton = untitledSaveButton({
    name,
    blocked: schemaSaveReason(draft),
    alreadySaved,
    renameSaveDisabled: rename.saveDisabled,
    saving,
  });
  // Direct save under the row's pending name; a rejection (duplicate or
  // failed write) opens the name input red with the message.
  const save = async () => {
    if (saving) return;
    setSaving(true);
    const message = await onSave(rename.pendingName);
    setSaving(false);
    if (message) rename.openWithError(message);
  };
  return (
    <ItemRow
      data-testid={`${testId}-untitled-row`}
      $active={selected}
      $schema
      $interactive
      {...toggleActivationProps(onToggle)}
    >
      <SchemaRowPrefix />
      <SchemaItemBody>
        <LeadingIcon
          icon={Icon.Schema}
          active={selected}
          errored={errored}
          warning={warning}
        />
        {rename.nameNode}
      </SchemaItemBody>
      <RowActionsGroup>
        <RowActionButton
          label={saveButton.label}
          icon={Icon.Save}
          onClick={save}
          disabled={saveButton.disabled}
        />
        <RemoveRowButton name={name} onRemove={onDiscard} />
      </RowActionsGroup>
      {selected && <AccentBar />}
    </ItemRow>
  );
}

// Whether the slot draft's exact shape is already stored under the row's
// name — its Save would only mint a duplicate, so the icon disables (the
// dropped source's seed after that schema was saved once already). The
// definition rides react-query: one cached fetch for the name-matched
// stored schema, none when no name matches.
function useDraftAlreadySaved(
  serviceName: string | null,
  name: string,
  draft: string,
): boolean {
  const { byService } = useSchemasByService(
    useMemo(() => (serviceName ? [serviceName] : []), [serviceName]),
  );
  const schemas = serviceName ? byService[serviceName]?.data : undefined;
  const match = schemas?.find((entry) => (entry.name ?? entry.id) === name);
  const { data: entry } = useSchema(serviceName, match?.id ?? null);
  return entry != null && schemaContentEqual(draft, entry.definition);
}

// The selectable "use the editor" pseudo-row (Figma 15683-307084): ↳ +
// badge, then the italic invitation with no leading icon.
function SchemaPlaceholderRow({
  testId,
  selected,
  onToggle,
}: {
  testId: TestId;
  selected: boolean;
  onToggle: () => void;
}) {
  return (
    <ItemRow
      data-testid={`${testId}-editor-row`}
      $active={selected}
      $schema
      $interactive
      {...toggleActivationProps(onToggle)}
    >
      <SchemaRowPrefix emphasis={selected} />
      <SchemaItemBody>
        <PseudoText $emphasis={selected}>
          Drag it from the explorer or use the editor
        </PseudoText>
      </SchemaItemBody>
      {selected && <AccentBar />}
    </ItemRow>
  );
}

// The empty slot of a SCHEMA sub-section: the drop zone while a schema drag
// runs, the unsaved "Untitled schema*" row while the editor holds a draft,
// else the selectable "use the editor" placeholder.
function EmptySchemaSlot({
  testId,
  homeService,
  schemaDragActive,
  hasDraft,
  name,
  draft,
  startNaming,
  onStartNamingConsumed,
  editorSelected,
  onToggleEditorRow,
  onRename,
  validateRename,
  onSave,
  onDiscard,
  onDropSchema,
  warning = false,
  seeding = false,
}: {
  testId: TestId;
  homeService: string | null;
  schemaDragActive: boolean;
  hasDraft: boolean;
  /** The stage's drop-time schema seed is still resolving — the slot
   *  reads "in progress" instead of inviting a drag (schemaSeeding). */
  seeding?: boolean;
  /** The untitled row's display name (local until saved). */
  name: string;
  /** The slot draft's text, for the already-saved Save gate. */
  draft: SchemaBody;
  startNaming: boolean;
  onStartNamingConsumed: () => void;
  editorSelected: boolean;
  onToggleEditorRow: () => void;
  onRename: (name: string) => Promise<boolean>;
  validateRename: (name: string) => string | null;
  /** Direct save under the given name; resolves an error message for the
   *  name input, or null on success. */
  onSave: (name: string) => Promise<string | null>;
  onDiscard: () => void;
  onDropSchema: (resource: StagedResource) => void;
  /** The untitled draft here is unreferenced content (bottom block). */
  warning?: boolean;
}) {
  if (schemaDragActive) {
    return (
      <ItemRow $zone>
        <ExplorerDropZone
          testId={`${testId}-drop-zone`}
          shownDuringDrag
          canDrop={(data) => data.isSchema === true}
          onDrop={({ id, name, icon }) => onDropSchema({ id, name, icon })}
        />
      </ItemRow>
    );
  }
  if (hasDraft) {
    return (
      <UntitledSchemaRow
        testId={testId}
        homeService={homeService}
        name={name}
        draft={draft}
        selected={editorSelected}
        startNaming={startNaming}
        onStartNamingConsumed={onStartNamingConsumed}
        onToggle={onToggleEditorRow}
        onRename={onRename}
        validateRename={validateRename}
        onSave={onSave}
        onDiscard={onDiscard}
        warning={warning}
      />
    );
  }
  if (seeding) {
    return <SeedingSchemaRow testId={testId} />;
  }
  return (
    <SchemaPlaceholderRow
      testId={testId}
      selected={editorSelected}
      onToggle={onToggleEditorRow}
    />
  );
}

// The slot while the dropped stage's schema seed resolves (schemaSeeding):
// ↳ + badge, then the AI Helper's jumping dots where the schema icon
// would sit and "in progress" in the pseudo-row italic where the name
// would. Not interactive — there is nothing to select yet.
function SeedingSchemaRow({ testId }: { testId: TestId }) {
  return (
    <ItemRow data-testid={`${testId}-seeding-row`} $schema aria-busy="true">
      <SchemaRowPrefix />
      <SchemaItemBody>
        <SeedingDots />
        <PseudoText>in progress</PseudoText>
      </SchemaItemBody>
    </ItemRow>
  );
}

// The dots keep the AI Helper's own size and spacing; only the color is
// the row's placeholder gray.
const SeedingDots = styled(JumpingDots)`
  color: ${(p) => p.theme.semanticTextPlaceholder};
`;

// The sub-section's save flows in one bundle: the untitled draft's Save
// modal, the shared-schema warning, the "Save as" fork, and the per-block
// row-instance helpers.
function useSchemaSlot(options: {
  editorRowId: RowId;
  selectedId: string | null;
  onDropSchema: (resource: StagedResource) => void;
  onSaveDraft: (resource: StagedResource) => void;
  onSaveAsReplaced?: (oldSchemaId: RowId) => void;
}) {
  const { editorRowId, selectedId, onDropSchema, onSaveAsReplaced } = options;
  const { schemaDrafts, discardSchemaDraft, clearSchemaDraft } =
    usePipelineStaging();
  // Row Save with the schema referenced elsewhere: the warning's subject,
  // or null. "Save as" moves the same schema into the name modal.
  const [warnFor, setWarnFor] = useState<StagedSchema | null>(null);
  const [saveAsFor, setSaveAsFor] = useState<StagedSchema | null>(null);
  // The slot's unsaved draft persists in the staging context, so the
  // untitled row stays in its block across selection changes and new
  // blocks.
  const draft = schemaDrafts[editorRowId] ?? "";
  const saving = useSchemaSaving(
    editorRowId,
    selectedId,
    draft,
    options.onSaveDraft,
  );
  const openSaveAs = (schema: StagedSchema | null) => setSaveAsFor(schema);
  // Rows select as per-block instances (this sub-section's editorRowId is
  // the block key): the same schema in another block stays unselected and
  // never shows this instance's dirty asterisk. An instance draft exists
  // exactly while the row's text diverges from its stored definition —
  // and survives leaving the row, so the asterisk does too.
  const instanceId = (schema: StagedSchema) =>
    schemaInstanceId(schema.id, editorRowId);
  const isDirty = (schema: StagedSchema) =>
    schemaDrafts[instanceId(schema)] != null;
  const saveBlockReason = (schema: StagedSchema): string | null => {
    const rowDraft = schemaDrafts[instanceId(schema)];
    return rowDraft == null ? null : schemaSaveReason(rowDraft);
  };

  // Saving a schema other parts of the pipeline also use changes all of
  // them — warn first; a single-reference schema saves straight away. The
  // edits come from the row's instance draft (no draft = disabled button).
  const onRowSave = (schema: StagedSchema) => {
    const rowDraft = schemaDrafts[instanceId(schema)];
    if (rowDraft == null || schemaSaveReason(rowDraft) !== null) return;
    if (referenceCount(schema) > 1) {
      setWarnFor(schema);
      return;
    }
    saving.saveRow(schema, instanceId(schema), rowDraft);
  };

  // The warning's Continue: save the edits everywhere the schema is used.
  const onWarnConfirm = () => {
    if (warnFor) {
      const rowDraft = schemaDrafts[instanceId(warnFor)];
      if (rowDraft != null) {
        saving.saveRow(warnFor, instanceId(warnFor), rowDraft);
      }
    }
    setWarnFor(null);
  };

  // The "Save as" fork: persist the edits as a NEW schema next to the
  // source and swap it into this block only (a group attach evicts the old
  // schema from the slot; the bottom block clears the old waiting copy via
  // onSaveAsReplaced).
  const onSaveAs = async (name: SchemaName) => {
    if (!saveAsFor) return;
    const rowDraft = schemaDrafts[instanceId(saveAsFor)];
    if (rowDraft == null) return;
    const resource = await saving.saveAs(saveAsFor, name, rowDraft);
    if (!resource) return;
    onDropSchema(resource);
    onSaveAsReplaced?.(saveAsFor.id);
    // The fork owns the edits now — the old row's draft is obsolete.
    clearSchemaDraft(instanceId(saveAsFor));
  };

  return {
    draft,
    saving,
    instanceId,
    isDirty,
    saveBlockReason,
    onRowSave,
    onSaveAs,
    onWarnConfirm,
    warnFor,
    setWarnFor,
    saveAsFor,
    openSaveAs,
    discardDraft: () => discardSchemaDraft(editorRowId),
  };
}

type SchemaSlot = ReturnType<typeof useSchemaSlot>;

// A rename's rejection message straight from validateName's duplicate
// check, or null when the name is fine — the staged row's rename and the
// placeholder row's rename/Save all read the same first message.
function nameRejection(
  validateName: SchemaSlot["saving"]["validateName"],
  name: string,
): string | null {
  return validateName(name)?.messages?.[0] ?? null;
}

// ⌘S while one of this block's schema rows is selected: the slot's
// untitled draft saves under its row name, a dirty staged instance runs
// the row save. Registration is render-driven — the selected block wins.
function useSchemaSaveHotkey(options: {
  slot: SchemaSlot;
  schemas: StagedSchema[];
  selectedId: string | null;
  editorRowId: RowId;
  draftName: string;
}) {
  const { slot, schemas, selectedId, editorRowId, draftName } = options;
  const { setActiveSaveHandler } = usePipelineStaging();
  useEffect(() => {
    if (selectedId === editorRowId && slot.draft.trim() !== "") {
      setActiveSaveHandler(() => void saveNewSchema(slot, draftName));
      return () => setActiveSaveHandler(null);
    }
    const instance = schemas.find(
      (schema) => slot.instanceId(schema) === selectedId,
    );
    if (instance && slot.isDirty(instance)) {
      setActiveSaveHandler(() => slot.onRowSave(instance));
      return () => setActiveSaveHandler(null);
    }
    return undefined;
  });
}

// The placeholder row's direct Save (no modal): a name already stored
// UPDATES that schema in place — the draft is an edit of it, not a rival
// wanting another name; a fresh name persists a new entry. A failed
// write comes back red on the row's name input.
async function saveNewSchema(
  slot: SchemaSlot,
  name: string,
): Promise<string | null> {
  try {
    if (slot.saving.storedIdFor(name)) {
      await slot.saving.saveOver(name);
      return null;
    }
    const problem = nameRejection(slot.saving.validateName, name);
    if (problem) return problem;
    await slot.saving.saveNew(name);
  } catch (error) {
    return error instanceof Error ? error.message : "Failed to save schema";
  }
  return null;
}

// The Save-schema name modal ("Save as" fork only — the untitled draft
// saves directly under its row name) and the shared-schema warning,
// driven by the slot state above.
function SchemaSaveModals({ slot }: { slot: SchemaSlot }) {
  // The fork stays in the folder the source came from (schemas live per
  // service, TTTL-319), so the duplicate check reads that service's store.
  const forkService = slot.saveAsFor
    ? schemaForkService(slot.saveAsFor.id)
    : null;
  return (
    <>
      {/* Save schema modal (Figma 14502-134872) — names the row's
          "Save as" fork. */}
      <NameModal
        isOpen={slot.saveAsFor !== null}
        onClose={() => {
          slot.openSaveAs(null);
        }}
        onSave={slot.onSaveAs}
        title="Save schema"
        label="Schema name"
        placeholder="Add a name to the schema"
        emptyMessage="Add a name to the schema"
        errorFallback="Failed to save schema"
        testIdBase="save-schema"
        validateName={(name) => slot.saving.validateName(name, forkService)}
      />

      {/* Saving a schema that other parts of the pipeline also use changes
          all of them — Continue saves everywhere, Save as forks a new
          schema for this block only. The message wears the Warning alert
          treatment, like the panel's unreferenced-schema alert. */}
      <ConfirmDeleteModal
        isOpen={slot.warnFor !== null}
        title="Save schema"
        message={
          <StageAlert
            state="Warning"
            icon
            dismissible={false}
            width="100%"
            message={`"${slot.warnFor?.name ?? ""}" is used in other parts of the pipeline — saving these changes will change it everywhere it is used.`}
          />
        }
        confirmLabel="Continue"
        confirmType="primary"
        loadingLabel="Saving…"
        extraAction={{
          label: "Save as",
          onClick: () => {
            if (slot.warnFor) slot.openSaveAs(slot.warnFor);
            slot.setWarnFor(null);
          },
        }}
        onConfirm={slot.onWarnConfirm}
        onCancel={() => slot.setWarnFor(null)}
      />
    </>
  );
}

export function SchemaSubSection({
  testId,
  editorRowId,
  schemas,
  schemaDragActive,
  selectedId,
  onToggleSelected,
  onUnstage,
  onDropSchema,
  onSaveDraft = onDropSchema,
  onSaveAsReplaced,
  unreferenced = false,
}: {
  testId: TestId;
  editorRowId: RowId;
  schemas: StagedSchema[];
  schemaDragActive: boolean;
  selectedId: string | null;
  onToggleSelected: (id: RowId) => void;
  onUnstage: (id: RowId) => void;
  onDropSchema: (resource: StagedResource) => void;
  /** Where a schema saved from this block's editor draft is staged; drops
   *  keep using onDropSchema. Defaults to onDropSchema. */
  onSaveDraft?: (resource: StagedResource) => void;
  /** Extra cleanup after a "Save as" fork replaced this block's schema —
   *  the bottom block removes the old waiting copy (group slots are
   *  handled by attach eviction). */
  onSaveAsReplaced?: (oldSchemaId: RowId) => void;
  /** The tab's bottom block: its rows are content Run ignores unless the
   *  schema is also attached to a stage (StageNavPanel's
   *  hasUnreferencedContent) — those rows wear the warning glyph. */
  unreferenced?: boolean;
}) {
  const slot = useSchemaSlot({
    editorRowId,
    selectedId,
    onDropSchema,
    onSaveDraft,
    onSaveAsReplaced,
  });
  const editorSelected = selectedId === editorRowId;
  const {
    schemaDrafts,
    schemaDraftNames,
    setSchemaDraftName,
    freshSchemaDraftRowId,
    clearFreshSchemaDraft,
    seedingStageIds,
  } = usePipelineStaging();
  const draftName = schemaDraftNames[editorRowId] ?? "Untitled schema";
  useSchemaSaveHotkey({ slot, schemas, selectedId, editorRowId, draftName });
  // Parsed once per draft change, not per render: the erroring rows of
  // this block, by instance id.
  const erroredInstanceIds = useMemo(() => {
    const ids = new Set<RowId>();
    for (const schema of schemas) {
      const id = schemaInstanceId(schema.id, editorRowId);
      if (jsonErrorRows(schemaDrafts[id] ?? "").length > 0) ids.add(id);
    }
    return ids;
  }, [schemas, schemaDrafts, editorRowId]);

  return (
    <SubSection data-testid={testId}>
      {schemas.map((schema) => (
        <StagedSchemaRow
          key={schema.id}
          schema={schema}
          selected={selectedId === slot.instanceId(schema)}
          dirty={slot.isDirty(schema)}
          saveBlocked={slot.saveBlockReason(schema)}
          errored={erroredInstanceIds.has(slot.instanceId(schema))}
          warning={unreferenced && schema.attachments.length === 0}
          onToggleSelected={() => onToggleSelected(slot.instanceId(schema))}
          onSave={() => slot.onRowSave(schema)}
          onUnstage={onUnstage}
          onRename={(name) => slot.saving.renameRow(schema, name)}
          // The row's own store decides what counts as a duplicate — the
          // schema may live on another service than the block's home.
          validateRename={(name) =>
            nameRejection(
              (n) =>
                slot.saving.validateName(
                  n,
                  schemaForkService(schema.id) ?? slot.saving.homeService,
                ),
              name,
            )
          }
        />
      ))}

      {/* One schema per block: the placeholder/drop slot exists only while
          the slot is empty (14442-316243). */}
      {schemas.length === 0 && (
        <EmptySchemaSlot
          testId={testId}
          homeService={slot.saving.homeService}
          schemaDragActive={schemaDragActive}
          hasDraft={slot.draft !== ""}
          name={draftName}
          draft={slot.draft}
          startNaming={freshSchemaDraftRowId === editorRowId}
          onStartNamingConsumed={clearFreshSchemaDraft}
          editorSelected={editorSelected}
          onToggleEditorRow={() => onToggleSelected(editorRowId)}
          // A draft rename is client-side only — the stored schema is
          // born at Save, directly under the row's name.
          onRename={async (name) => {
            setSchemaDraftName(editorRowId, name);
            return true;
          }}
          validateRename={(name) =>
            nameRejection(slot.saving.validateName, name)
          }
          // Direct save — no modal: a taken name or failed write comes
          // back red on the row's name input.
          onSave={(name) => saveNewSchema(slot, name)}
          onDiscard={slot.discardDraft}
          onDropSchema={onDropSchema}
          warning={unreferenced}
          seeding={slotSeeding(seedingStageIds, editorRowId)}
        />
      )}

      <SchemaSaveModals slot={slot} />
    </SubSection>
  );
}
